import React from 'react';
import { TableColumn } from './types';
import StatusPill from '../StatusPill';
import { Eye, Pencil } from 'lucide-react';
import { isVisitEditable } from '../../utils/dateUtils';
export interface JobsiteAddressDTO {
  address1: string;
  address2: string | null;
  city: string;
  postalCode: string;
  province: string;
  job_id: string;
  boardArea?: string;
  coordinates: number[];
  userId: string; // Added userId
}

export const getReportColumns = (reportType: string, userRole?: string | null, navigate?: (path: string) => void): TableColumn[] => {
  if (reportType === 'Monthly Organizer Activity Report') {
    const columns: TableColumn[] = [
      {
        label: 'Type',
        accessor: 'type',
        render: (value: string) => value || '-'
      }
    ];

    // Only show Organizer column for non-organizer roles
    if (userRole !== 'organizer') {
      columns.push({
        label: 'Organizer',
        accessor: 'organizerName',
        sortable: true,
        render: (value: string) => value || '-'
      });
    }

    columns.push(
      {
        label: 'Jobsite',
        accessor: 'jobSiteName',
        sortable: true,
        render: (value: string) => {
          if (!value) return '-';
          const fullJobsite = value;
          const displayJobsite = fullJobsite.length > 50 ? fullJobsite.substring(0, 50) + '...' : fullJobsite;
          return <span title={fullJobsite}>{displayJobsite}</span>;
        }
      },
      {
        label: 'Province',
        accessor: 'province',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'Union Status',
        accessor: 'unionType',
        render: (value: string) => value || '-'
      },
      {
        label: 'General Contractor',
        accessor: 'contractorName',
        render: (value: string) => {
          if (!value) return '-';
          const fullContractor = value;
          const displayContractor = fullContractor.length > 50 ? fullContractor.substring(0, 50) + '...' : fullContractor;
          return <span title={fullContractor}>{displayContractor}</span>;
        }
      },
      {
        label: 'Local Unions',
        accessor: 'localUnions',
        render: (value: { code: string; unionNumber: string }[]) => {
          const fullLocalUnions = value.map((localUnion) => `${localUnion.code} - ${localUnion.unionNumber}`).join(', ') || '-';
          const displayLocalUnions = fullLocalUnions.length > 30 ? fullLocalUnions.substring(0, 30) + '...' : fullLocalUnions;
          return <span title={fullLocalUnions}>{displayLocalUnions}</span>;
        }
      },
      {
        label: 'Date',
        accessor: 'dateOfVisit',
        sortable: true,
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      },
      {
        label: 'Notes/Comments',
        accessor: 'notes',
        render: (value: string[]) => {
          if (!value || value.length === 0) return '-';
          const fullNotes = value.join('; ');
          let displayNotes = fullNotes;
          if (fullNotes.length > 30) {
            displayNotes = fullNotes.substring(0, 30) + '...';
          }
          return <span title={fullNotes}>{displayNotes}</span>;
        }
      }
    );

    return columns;
  } else if (reportType === 'Jobsite List Report by Organizer') {
    return [
      {
        label: 'Organizer Name',
        accessor: 'organizerName',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'Jobsite Name',
        accessor: 'jobSiteName',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'Jobsite Status',
        accessor: 'status',
        sortable: true,
        render: (value: string) => value ? <StatusPill status={value} /> : '-'
      },
      {
        label: 'Province',
        accessor: 'province',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'Union Type',
        accessor: 'unionType',
        render: (value: string) => value || '-'
      },
      {
        label: 'Contractor Name',
        accessor: 'contractorName',
        render: (value: string) => value || '-'
      },
      {
        label: 'Local Unions',
        accessor: 'localUnions',
        render: (value: { code: string; unionNumber: string }[]) => value.map((localUnion) => `${localUnion.code} - ${localUnion.unionNumber}`).join(', ') || '-'
      },
      {
        label: 'Start Date',
        accessor: 'startDate',
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      },
      {
        label: 'End Date',
        accessor: 'endDate',
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      },
      {
        label: 'Last Updated',
        accessor: 'lastUpdated',
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      }
    ];
  } else if (reportType === 'Jobsite List by Union') {
    return [
      {
        label: 'Jobsite Name',
        accessor: 'jobSiteName',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'Sector',
        accessor: 'sector',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'Province',
        accessor: 'province',
        render: (value: string) => value || '-'
      },
      {
        label: 'Address',
        accessor: 'address',
        render: (value: JobsiteAddressDTO) => {
          if (!value) return '-';
          if (typeof value === 'string') return value;
          return `${value.address2 ? `${value.address2} - ` : ''}${value.address1 || ''}, ${value.city || ''}`.replace(/^,\s*|,\s*$/g, '') || '-';
        }
      },
      {
        label: 'Jobsite Status',
        accessor: 'status',
        sortable: true,
        render: (value: string) => value ? <StatusPill status={value} /> : '-'
      },
      { label: 'Contractor Name', accessor: 'contractorName', render: (value: string) => value || '-' },
      { label: 'Contractor Email', accessor: 'contractorEmail', render: (value: string) => value || '-' },
      {
        label: 'Local Unions',
        accessor: 'localUnions',
        render: (value: { code: string; unionNumber: string }[]) => value.map((localUnion) => `${localUnion.code} - ${localUnion.unionNumber}`).join(', ') || '-'
      },
      { label: 'Union Type', accessor: 'unionType', render: (value: string) => value || '-' },
      { label: 'Is Signatory', accessor: 'isSignatory', render: (value: string) => value || '-' },
      { label: 'Jobsite Progress', accessor: 'jobSiteProgress', render: (value: string) => value || '-' },
      {
        label: 'Start Date',
        accessor: 'startDate',
        sortable: true,
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      },
      {
        label: 'Total Workers',
        accessor: 'totalWorkers',
        render: (value: string) => value || '-'
      },
      {
        label: 'Last Updated',
        accessor: 'lastUpdated',
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      },
      {
        label: 'Notes/Comments',
        accessor: 'notes',
        render: (value: string[]) => {
          if (!value || value.length === 0) return '-';
          const fullNotes = value.join('; ');
          let displayNotes = fullNotes;
          if (fullNotes.length > 30) {
            displayNotes = fullNotes.substring(0, 30) + '...';
          }
          return <span title={fullNotes}>{displayNotes}</span>;
        }
      }
    ];
  } else if (reportType === 'Jobsite Visits Report') {
    return [
      {
        label: 'Actions',
        accessor: 'actions',
        render: (value: any, row: any) => {
          if (!navigate) return '-';
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/jobsite-management/visit-details/${row.id}`)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                title="View Visit"
              >
                <Eye className="w-3 h-3 mr-1" />
                View
              </button>
              <button
                disabled={userRole === 'interRepre' || !row.visitDate || (userRole !== 'admin' && !isVisitEditable(row.visitDate))}
                onClick={() => navigate(`/jobsite-management/details/${row.jobId}/edit-visit/${row.id}`)}
                className={`inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded transition-colors ${
                  userRole === 'interRepre' || !row.visitDate || (userRole !== 'admin' && !isVisitEditable(row.visitDate))
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-60'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                }`}
                title={
                  userRole === 'interRepre'
                    ? "You do not have permission to edit visits"
                    : (userRole !== 'admin' && (!row.visitDate || !isVisitEditable(row.visitDate)))
                    ? "This visit is older than 60 days and cannot be edited"
                    : "Edit Visit"
                }
              >
                <Pencil className="w-3 h-3 mr-1" />
                Edit
              </button>
            </div>
          );
        }
      },
      {
        label: 'Organizer',
        accessor: 'organizername',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'Jobsite',
        accessor: 'jobsite',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'Address',
        accessor: 'address',
        sortable: true,
        render: (value: string | JobsiteAddressDTO) => {
          if (!value) return '-';
          let fullAddress = '';
          if (typeof value === 'string') {
            fullAddress = value;
          } else {
            fullAddress = `${value.address2 ? `${value.address2} - ` : ''}${value.address1 || ''}, ${value.city || ''}`.replace(/^,\s*|,\s*$/g, '') || '-';
          }
          const displayAddress = fullAddress.length > 35 ? fullAddress.substring(0, 35) + '...' : fullAddress;
          return <span title={fullAddress}>{displayAddress}</span>;
        }
      },
      {
        label: 'Province',
        accessor: 'province',
        sortable: true,
        render: (value: string) => value || '-'
      },
      {
        label: 'General Contractor',
        accessor: 'contractor',
        sortable: true,
        render: (value: string) => {
          if (!value) return '-';
          const fullContractor = value;
          const displayContractor = fullContractor.length > 50 ? fullContractor.substring(0, 50) + '...' : fullContractor;
          return <span title={fullContractor}>{displayContractor}</span>;
        }
      },
      {
        label: 'Completion Status',
        accessor: 'completionStatus',
        render: (value: string) => value || '-'
      },
      {
        label: 'Visit Date',
        accessor: 'visitDate',
        sortable: true,
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      }
    ];
  } else {
    // Default columns for Jobsite Progress Report
    return [
      {
        label: 'Jobsite Name',
        accessor: 'jobSiteName',
        sortable: true,
        render: (value: string) => value || '-'
      },
      { label: 'Sector', accessor: 'sector', render: (value: string) => value || '-' },
      { label: 'Province', accessor: 'province', sortable: true, render: (value: string) => value || '-' },
      {
        label: 'Address',
        accessor: 'address',
        render: (value: JobsiteAddressDTO) => {
          if (!value) return '-';
          if (typeof value === 'string') return value;
          return `${value.address2 ? `${value.address2} - ` : ''}${value.address1 || ''}, ${value.city || ''}`.replace(/^,\s*|,\s*$/g, '') || '-';
        }
      },
      {
        label: 'Jobsite Status',
        accessor: 'status',
        sortable: true,
        render: (value: string) => value ? <StatusPill status={value} /> : '-'
      },
      { label: 'Contractor Name', accessor: 'contractorName', render: (value: string) => value || '-' },
      { label: 'Contractor Email', accessor: 'contractorEmail', render: (value: string) => value || '-' },
      {
        label: 'Local Unions',
        accessor: 'localUnions',
        render: (value: { code: string; unionNumber: string }[]) => value.map((localUnion) => `${localUnion.code} - ${localUnion.unionNumber}`).join(', ') || '-'
      },
      { label: 'Union Type', accessor: 'unionType', render: (value: string) => value || '-' },
      { label: 'Is Signatory', accessor: 'isSignatory', render: (value: string) => value || '-' },
      { label: 'Jobsite Progress', accessor: 'jobSiteProgress', render: (value: string) => value || '-' },
      {
        label: 'Start Date',
        accessor: 'startDate',
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      },
      {
        label: 'Last Updated',
        accessor: 'lastUpdated',
        render: (value: string) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'
      },
      {
        label: 'Notes/Comments',
        accessor: 'notes',
        render: (value: string[]) => {
          if (!value || value.length === 0) return '-';
          const fullNotes = value.join('; ');
          let displayNotes = fullNotes;
          if (fullNotes.length > 30) {
            displayNotes = fullNotes.substring(0, 30) + '...';
          }
          return <span title={fullNotes}>{displayNotes}</span>;
        }
      }
    ];
  }
};

export default getReportColumns;
